import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { JsonViewer } from '@/components/ui/json-viewer';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import type { DebugPayloadSkeleton } from '../chat-debug.types';

function stringSetting(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function ConfigRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string | null;
}) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2 py-1.5 border-b border-border last:border-b-0">
      <dt className="text-xs text-foreground-subtle">{label}</dt>
      <dd className="min-w-0 text-xs text-foreground">
        <div className="font-mono break-words">{value}</div>
        {detail && (
          <div className="mt-0.5 text-[10px] text-foreground-subtle">
            {detail}
          </div>
        )}
      </dd>
    </div>
  );
}

function RequestConfig({
  payload,
  requestIndex,
  showStep,
}: {
  payload: DebugPayloadSkeleton;
  requestIndex: number;
  showStep: boolean;
}) {
  const [showFullConfig, setShowFullConfig] = useState(false);
  const config = payload.agent_config;
  const configuredModel = stringSetting(config?.model);
  const requestModel = payload.model ?? configuredModel ?? 'Not captured';
  const alias = stringSetting(config?.alias);
  const rawBrowserConfig = config?.browser_use_config;
  const browserConfig =
    rawBrowserConfig && typeof rawBrowserConfig === 'object'
      ? (rawBrowserConfig as Record<string, unknown>)
      : null;
  const browserRuntime = stringSetting(browserConfig?.runtime);
  const browserController = stringSetting(browserConfig?.controller);
  const stagehandModel = stringSetting(browserConfig?.stagehand_model);
  const browserSummary =
    browserRuntime && browserController
      ? `${titleCase(browserRuntime)} + ${titleCase(browserController)}`
      : config
        ? 'Not configured'
        : 'Not captured';
  const browserDetail = stagehandModel
    ? `Stagehand model ${stagehandModel}`
    : null;

  return (
    <YStack
      className="gap-2 rounded-md border border-border bg-surface/30 p-2.5"
      aria-label={`Captured agent configuration for step ${(payload.step_index ?? 0) + 1}`}
    >
      {showStep && (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground-subtle">
          Step {(payload.step_index ?? 0) + 1}
        </span>
      )}
      <dl>
        <ConfigRow
          label="Agent ID"
          value={payload.agent_id ?? 'Not captured'}
        />
        {alias && <ConfigRow label="Alias" value={alias} />}
        <ConfigRow label="Model" value={requestModel} />
        {configuredModel && configuredModel !== requestModel && (
          <ConfigRow label="Configured model" value={configuredModel} />
        )}
        <ConfigRow
          label="browser_use_config"
          value={browserSummary}
          detail={browserDetail}
        />
      </dl>

      {config ? (
        <Collapsible open={showFullConfig} onOpenChange={setShowFullConfig}>
          <CollapsibleTrigger className="w-full">
            <XStack className="items-center gap-1.5 text-xs font-medium text-foreground-muted hover:text-foreground">
              <Icons.chevronRight
                className={cn(
                  'size-3 transition-transform',
                  showFullConfig && 'rotate-90'
                )}
              />
              <span>Full resolved config</span>
            </XStack>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <JsonViewer
              data={{
                agent_id: payload.agent_id,
                request_model: payload.model,
                agent_config: config,
              }}
              id={`agent-config-step-${payload.step_index ?? 'unknown'}-request-${requestIndex}`}
              maxHeight="lg"
              className="!p-2"
            />
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <span className="text-[10px] text-foreground-subtle">
          Resolved config was not captured for this request.
        </span>
      )}
    </YStack>
  );
}

export function AgentConfigViewer({
  payloads,
}: {
  payloads: DebugPayloadSkeleton[];
}) {
  if (!payloads.length) {
    return (
      <span className="text-xs text-foreground-subtle">
        No captured requests for this message.
      </span>
    );
  }

  return (
    <YStack className="gap-2">
      {payloads.map((payload, index) => (
        <RequestConfig
          key={`${payload.message_id}:${payload.step_index ?? 'unknown'}:${payload.created_at}:${index}`}
          payload={payload}
          requestIndex={index}
          showStep={payloads.length > 1}
        />
      ))}
    </YStack>
  );
}
